import { upperFirst } from 'lodash'
import React, { useContext, useEffect, useState } from 'react'

import api from 'src/api'
import {
  AgencySettings,
  AgencySettings_EnabledExtensionsEnum,
  AgencySettings_EnabledFeaturesEnum,
  AgencySettings_VehiclePropulsionsEnum,
  AgencySettings_VehicleTypesEnum,
  AgencySettings_AllowedVehicleTypesEnum,
  Policy_KindEnum,
  PolicyRule_RuleUnitsEnum,
} from 'src/api/gen/api'
import { CurrencyKind, DistanceUnit, DistanceUnitSymbol, SpeedUnit, SpeedUnitSymbol } from 'src/api/types/units'
import { getTranslation } from 'src/commons/i18n'
import { currencySymbols, enumsTranslationKeys, ICurrencySymbols, PREFIXED_CURRENCIES } from 'src/commons/i18nEnums'
import { getTemporalDurationLike, getTemporalRange, ITemporalRange } from 'src/commons/temporalHelpers'
import { GranularityEnum } from 'src/commons/types'

export interface IProps {
  children?: React.ReactNode
}

export interface IAgencySettingsContext extends AgencySettings {
  allowed_vehicle_types: AgencySettings_AllowedVehicleTypesEnum[]
  get_kpi_filter_range: () => ITemporalRange
  isLoaded: boolean
  // logo: string
  // map_config: {
  //   center: {
  //     latitude: number
  //     longitude: number
  //   }
  //   zoom: number
  // }
  // name: string
  mds_compatible_kinds: string[]
  penalty_symbol: string
  permit_currency_symbol: string
  vehicle_propulsions: AgencySettings_VehiclePropulsionsEnum[]
  vehicle_types: AgencySettings_VehicleTypesEnum[]
}

const initialAgencySettings: IAgencySettingsContext = {
  isLoaded: false,
  // name: '',
  // logo: '',
  language_code: 'en-us',
  penalty_currency_code: 'USD',
  penalty_symbol: '$',
  speed_unit: 'mph',
  speed_unit_display: 'mph',
  distance_unit: 'miles',
  permit_currency_code: 'USD',
  permit_currency_symbol: '$',
  smart_patrol_parking_threshold: 0.0,
  trip_threshold: 0,
  enabled_extensions: [] as AgencySettings_EnabledExtensionsEnum[],
  enabled_features: [] as AgencySettings_EnabledFeaturesEnum[],
  vehicle_types: [],
  allowed_vehicle_types: [],
  vehicle_propulsions: [],
  vehicle_states: [],
  event_types: [],
  kpi_filter_number_of_units: 7,
  kpi_filter_unit: 'day',
  kpi_filter_granularity: 'day',
  get_kpi_filter_range: () => getTemporalRange({ days: 7 }, true),
  device_type_to_category_mapping: '',
  kpi_infrastructure_train_distance: 100,
  kpi_infrastructure_tram_distance: 50,
  kpi_infrastructure_subway_distance: 50,
  line_types: [],
  mds_compatible_kinds: [],
}

export const AgencySettingsContext = React.createContext<IAgencySettingsContext>(initialAgencySettings)

const AgencySettingsWrapper = (props: IProps) => {
  const { children } = props
  const [agencySettings, setAgencySettings] = useState<IAgencySettingsContext>(initialAgencySettings)

  const getSettings = async () => {
    try {
      const response = await api.agencySettings.agencySettingsList()

      const penalty_currency_code = response.settings.penalty_currency_code
      const penaltySymbol =
        penalty_currency_code in currencySymbols
          ? currencySymbols[penalty_currency_code as keyof ICurrencySymbols]
          : penalty_currency_code

      const permit_currency_code = response.settings.permit_currency_code
      const permitCurrencySymbol =
        permit_currency_code in currencySymbols
          ? currencySymbols[permit_currency_code as keyof ICurrencySymbols]
          : permit_currency_code

      const getKpiFilterRange = () =>
        getTemporalRange(
          getTemporalDurationLike(
            response.settings.kpi_filter_unit as GranularityEnum,
            response.settings.kpi_filter_number_of_units
          ),
          true,
          response.settings.kpi_filter_granularity as GranularityEnum
        )

      setAgencySettings({
        isLoaded: true,
        penalty_symbol: penaltySymbol,
        permit_currency_symbol: permitCurrencySymbol,
        get_kpi_filter_range: getKpiFilterRange,
        mds_compatible_kinds: [],
        allowed_vehicle_types: [],
        ...response.settings,
      })
    } catch (ex) {
      console.error(ex)
    }
  }

  useEffect(() => {
    getSettings()
  }, [])

  return <AgencySettingsContext.Provider value={agencySettings}>{children}</AgencySettingsContext.Provider>
}

export const useAgencySettings = (): IAgencySettingsContext => useContext(AgencySettingsContext)

export const useIsAgencySettingsLoaded = (): boolean => useContext(AgencySettingsContext).isLoaded

export const useAgencySettingsSpeedUnit = (): {
  symbol: SpeedUnitSymbol
  value: SpeedUnit
} => {
  const speedUnit = useContext(AgencySettingsContext).speed_unit
  if (speedUnit === PolicyRule_RuleUnitsEnum.kmh) {
    return {
      value: SpeedUnit.KilometersPerHour,
      symbol: SpeedUnitSymbol.KilometersPerHour,
    }
  } else {
    return {
      value: SpeedUnit.MilesPerHour,
      symbol: SpeedUnitSymbol.MilesPerHour,
    }
  }
}

export const useAgencySettingsDistanceUnit = (): {
  symbol: DistanceUnitSymbol
  value: DistanceUnit
} => {
  const speedUnit = useContext(AgencySettingsContext).speed_unit
  if (speedUnit === PolicyRule_RuleUnitsEnum.kmh) {
    return { value: DistanceUnit.Kilometer, symbol: DistanceUnitSymbol.Kilometer }
  } else {
    return { value: DistanceUnit.Mile, symbol: DistanceUnitSymbol.Mile }
  }
}

export function formatCurrency(amount: string | number, currencyCode: string, currencySymbol?: string): string {
  // This function will have to be changed later on to have a more robust one.
  if (PREFIXED_CURRENCIES.includes(currencyCode)) {
    return `${currencySymbol ? currencySymbol : currencyCode} ${amount}`
  }
  return `${amount} ${currencySymbol ? currencySymbol : currencyCode}`
}

export function _buildCurrencyFormatter(kind: CurrencyKind, agencySettings: IAgencySettingsContext) {
  let code: string
  let symbol: string

  if (kind === CurrencyKind.Permit) {
    ;({ permit_currency_code: code, permit_currency_symbol: symbol } = agencySettings)
  } else {
    ;({ penalty_currency_code: code, penalty_symbol: symbol } = agencySettings)
  }

  return (x: string | number) => formatCurrency(x, code, symbol)
}

export function useCurrencyFormatter(kind: CurrencyKind) {
  const agencySettings = useAgencySettings()
  return _buildCurrencyFormatter(kind, agencySettings)
}

export const POLICY_KIND_TO_FEATURE: Record<string, AgencySettings_EnabledFeaturesEnum> = {
  absolute_fleet_size: AgencySettings_EnabledFeaturesEnum['mds_policy.absolute_fleet_size'],
  fleet_budget: AgencySettings_EnabledFeaturesEnum['mds_policy.fleet_budget'],
  old_permit: AgencySettings_EnabledFeaturesEnum['mds_policy.old_permit'],
  time: AgencySettings_EnabledFeaturesEnum['mds_policy.time'],
  speed: AgencySettings_EnabledFeaturesEnum['mds_policy.speed'],
  user: AgencySettings_EnabledFeaturesEnum['mds_policy.user'],
  geofence: AgencySettings_EnabledFeaturesEnum['mds_policy.geofence'],
  parking: AgencySettings_EnabledFeaturesEnum['mds_policy.parking'],
  abandoned: AgencySettings_EnabledFeaturesEnum['mds_policy.abandoned'],
  trip_taxation: AgencySettings_EnabledFeaturesEnum['mds_policy.trip_taxation'],
  sidewalk: AgencySettings_EnabledFeaturesEnum['mds_policy.sidewalk'],
  balancing: AgencySettings_EnabledFeaturesEnum['mds_policy.balancing'],
  beginner_speed_limit: AgencySettings_EnabledFeaturesEnum['policies_beginner_mode.beginner_speed_limit'],
  beginner_allowed_hours: AgencySettings_EnabledFeaturesEnum['policies_beginner_mode.beginner_allowed_hours'],
  permit: AgencySettings_EnabledFeaturesEnum['fee_rates.permit'],
  vehicle_deployment: AgencySettings_EnabledFeaturesEnum['fee_rates.vehicle_deployment'],
  maintenance: AgencySettings_EnabledFeaturesEnum['mds_policy.maintenance'],
  dwell_time: AgencySettings_EnabledFeaturesEnum['mds_policy.dwell_time'],
}

export function useCheckEnabledFeature() {
  const { enabled_features = [] } = useAgencySettings()
  return (feature: AgencySettings_EnabledFeaturesEnum): boolean => enabled_features.includes(feature)
}

interface IEnabledFeature {
  children?: React.ReactNode
  feature: AgencySettings_EnabledFeaturesEnum
}

export function EnabledFeature({ children, feature }: IEnabledFeature) {
  const isFeatureEnabled = useCheckEnabledFeature()(feature)
  return isFeatureEnabled ? <>{children}</> : null
}

export interface IPolicyKind {
  id: Policy_KindEnum
  label: string
}

export function useEnabledPolicyKinds(): IPolicyKind[] {
  const { t } = getTranslation()
  const isFeatureEnabled = useCheckEnabledFeature()

  return Object.entries(enumsTranslationKeys.PolicyKind)
    .filter(([id]) => Object.keys(POLICY_KIND_TO_FEATURE).includes(id) && isFeatureEnabled(POLICY_KIND_TO_FEATURE[id]))
    .map(([id, label]) => ({ id: id as Policy_KindEnum, label: upperFirst(t(label)) }))
}

export default AgencySettingsWrapper
